import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPage?: string;
}

export function ProtectedRoute({ children, requiredPage }: ProtectedRouteProps) {
  const location = useLocation();
  const { canAccessPage, isLoading, currentUserRole } = useRole();
  
  // Exibir loading somente quando ainda não há role definido
  if (isLoading && !currentUserRole) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // Se não há role definido, redirecionar para dashboard
  if (!currentUserRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // Usar a página atual ou a página requerida para verificação
  const pageToCheck = requiredPage || location.pathname;
  
  // Verificar se pode acessar a página
  if (!canAccessPage(pageToCheck)) {
    // Para planejadores, redirecionar para página de registros
    if (currentUserRole === 'planner') {
      return <Navigate to="/planning" replace />;
    }
    
    // Para outros casos, mostrar página de acesso negado
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <CardTitle className="text-xl text-gray-900">Acesso Restrito</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              Você não tem permissão para acessar esta página.
            </p>
            <p className="text-sm text-gray-500">
              Seu papel atual: <span className="font-semibold">{getRoleName(currentUserRole)}</span>
            </p>
            <div className="pt-4">
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Voltar
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

// Função auxiliar para obter nome do role
function getRoleName(role: string) {
  switch (role) {
    case 'admin': return 'Administrador';
    case 'planner': return 'Planejador';
    case 'normal': return 'Normal';
    case 'member': return 'Membro';
    default: return role;
  }
}
